import React, { useState, useRef } from "react";
import "./contentpage.css";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";
import Question from "../components/Question";
import { Link } from "react-router-dom";
import BoxWithShadow from "../components/BoxWithShadow";

function IslamicTradePage15() {
  const contentRef = useRef(null);
    const links = {
    prev: "/islamic-trade-page-14", 
    index: "/index",                 
    next: "/islamic-trade-page-16", 
  };
  const titleText = "Rulings on Reconciliation (Sulh)";
  const questions = [
    {
      text: "1. What is the main purpose of Sulh (reconciliation) in Islamic law?",
      options: [
        {
          value: "A",
          label: "A. To delay disputes until a judge is appointed.",
        },
        {
          value: "B",
          label: "B. To restore harmony and resolve conflicts justly.",
        },
        {
          value: "C",
          label: "C. To reduce court procedures.",
        },
        {
          value: "D",
          label: "D. To divide inheritance fairly.",
        },
      ],
      correct: "B",
    },
    {
      text: "2. In Sulh in affirmation, who is allowed to waive a financial right for reconciliation?",
      options: [
        { value: "A", label: "A. Any family member." },
        { value: "B", label: "B. The debtor" },
        {
          value: "C",
          label: "C. The creditor with full ownership of the right",
        },
        { value: "D", label: "D. The local imam" },
      ],
      correct: "C",
    },
    {
      text: "3. Which of the following invalidates Sulh in negation??",
      options: [
        {
          value: "A",
          label: "A. The parties disagreeing over the amount owed",
        },
        { value: "B", label: "B. A misunderstanding about dates." },
        {
          value: "C",
          label: "C. One party intentionally denying rights with deceit.",
        },
        { value: "D", label: "D. One party seeking a delay in payment." },
      ],
      correct: "C",
    },
    {
      text: "4. According to the Quran, how should disputes between believers be handled?",
      options: [
        { value: "A", label: "A. Let them resolve it themselves." },
        { value: "B", label: "B. Postpone until a leader intervenes." },
        { value: "C", label: "C. Make peace between them." },
        { value: "D", label: "D.  Ignore minor conflicts." },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={15}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 15!</b> Today, we delve into the powerful and
        transformative concept of reconciliation i.e Sulh, as guided by the
        teachings of Islam. In a world where disputes are inevitable, Islam
        offers a merciful and just approach to conflict resolution that
        prioritizes peace, fairness, and social harmony. Through this lesson,
        you will learn how Sulh not only mends broken ties but also strengthens
        community bonds by upholding truth and justice. Whether disputes arise
        from misunderstanding, denial, or financial disagreements, today’s
        session will equip you with the foundational principles and ethical
        guidelines that ensure reconciliation is not only peaceful but
        righteous.
        <br />
        <br />
        <b>The Spirit and Significance of Sulh in Islam</b>
        <br />
        <br /> Sulh, meaning reconciliation or settlement, holds a central place
        in Islamic jurisprudence as a means of restoring peace and harmony
        between disputing parties. It is a practice deeply rooted in the Quran
        and Sunnah, reflecting the divine preference for peace over conflict and
        compassion over hostility. The Quran encourages believers to reconcile
        and maintain unity, stating:
        <br />
        “And if two groups among the believers fight, then make peace between
        them.”
        <a href="https://quran.com/49/9" target="blank">
          (Surah Al-Hujurat, 49:9)
        </a>
        <br />
        This verse underscores that conflict should never be allowed to fester
        within the Muslim community. Instead, believers are urged to intervene
        and mediate with fairness and sincerity to restore brotherhood. The
        Prophet Muhammad (peace be upon him) also emphasized the value of
        reconciliation in another hadith: “A liar is not one who tries to bring
        reconciliation among people and speaks good (in order to avert dispute),
        or he conveys good.”
        <a href="https://sunnah.com/muslim:2605a" target="blank">
          (Sahih Muslim, 2605)
        </a>
        <br />
        This remarkable teaching reveals the exceptional moral weight of
        peacebuilding in Islam. Even slight exaggerations or omissions, normally
        frowned upon, are permitted when they serve the noble goal of restoring
        harmony and preventing enmity. Islam, therefore, considers
        reconciliation not only a moral virtue but also a social necessity to
        ensure the collective well-being of the Ummah.
        <br />
        Sulh does not mean overlooking justice or compromising the truth.
        Instead, it provides a mechanism through which justice can be restored
        with wisdom and compassion. When disputes arise, whether between
        individuals, families, or communities, the process of Sulh involves
        dialogue, mutual understanding, and voluntary compromise, ensuring that
        both sides walk away with dignity intact.
        <br />
        <br />
        <b>Justice as the Foundation of Reconciliation</b>
        <br />
        <br />
        While reconciliation promotes peace, Islam insists that it must be
        grounded in justice. Without justice, reconciliation becomes fragile and
        insincere. The Quran commands fairness in all judgments, stating:
        <br />
        “Indeed, Allah commands you to render trusts to whom they are due and
        when you judge between people to judge with justice.”
        <a href="https://quran.com/4/58" target="blank">
          (Surah An-Nisa, 4:58)
        </a>
        <br />
        This principle guides the Islamic approach to conflict resolution: peace
        cannot come at the cost of truth. Therefore, when a dispute arises, the
        first step is to establish which party holds the rightful claim. Only
        after affirming justice should reconciliation be sought.
        <br />
        The role of the arbitrator or judge in such situations is crucial. The
        arbitrator must first affirm the correctness of the party whose claim is
        valid, ensuring that their rights are acknowledged. Once justice is
        established, the next step is to encourage forgiveness and
        reconciliation. This ensures that peace does not undermine truth, but
        rather emerges from it.
        <br />
        In practice, Sulh reflects both mercy and accountability. For example,
        if one party is owed money and the debtor genuinely struggles to repay,
        the creditor is encouraged to show compassion, perhaps by granting extra
        time or forgiving part of the debt. The Quran praises such gestures of
        mercy:
        <br />
        “If it is difficult for someone to repay a debt, postpone it until a
        time of ease. And if you waive it as an act of charity, it will be
        better for you, if only you knew.”
        <a href="https://quran.com/2/280" target="blank">
          (Surah Al-Baqarah 280)
        </a>{" "}
        In this way, reconciliation becomes a means not only to resolve material
        disputes but to elevate spiritual character. It promotes empathy and
        patience while ensuring that fairness remains intact.
        <br />
        <br />
        <b>Types of Reconciliation in Islamic Law</b>
        <br />
        <br />
        Islamic scholars categorize reconciliation into two main types: Sulh in
        affirmation and Sulh in negation. Both serve to restore peace but apply
        in different circumstances.
        <br />
        Sulh in affirmation occurs when both parties agree that one owes a
        financial obligation or right to the other. In this case, reconciliation
        may be achieved when the creditor voluntarily forgives the debt, either
        partially or fully, as an act of kindness. Importantly, this forgiveness
        must come from genuine willingness, not as a result of coercion or
        pressure. The Prophet Muhammad (peace be upon him) highly praised
        voluntary acts of forgiveness, and the Quran describes such actions as a
        form of charity that earns divine reward.
        <br />
        However, Islam sets boundaries to prevent misuse of reconciliation. For
        instance, a guardian managing the property of an orphan cannot forgive a
        debt owed to the orphan merely to settle a dispute, since doing so would
        infringe on the orphan’s rights. <br />
        The Prophet said: “Whoever takes charge of an orphan's property should
        not trade in it except with fairness, and he should not consume it
        unjustly.”{" "}
        <a href="https://sunnah.com/ibnmajah:2718" target="blank">
          (Sunan Ibn Majah, 2718)
        </a>
        <br />
        Sulh in negation, on the other hand, applies when one party denies the
        claim of the other. Such disputes often arise from misunderstanding,
        ignorance, or forgetfulness. In these cases, reconciliation is
        permissible if both parties willingly agree to a fair settlement. For
        instance, the claimant might accept a reduced amount or defer repayment
        to preserve goodwill. The Quran beautifully captures this spirit of
        brotherhood:
        <br />
        “The believers are but one brotherhood, so make peace between your
        brothers. And be mindful of Allah so you may be shown mercy.”{" "}
        <a href="https://quran.com/49/9" target="blank">
          (Surah Al-Hujurat, 49:10)
          <br />
        </a>
        However, reconciliation must never serve as a cover for deceit or
        injustice. If someone knowingly denies another’s rights and seeks
        reconciliation merely to avoid accountability, such a settlement is
        invalid.
        <br />
        The Prophet warned: “He who acted dishonestly towards us is not of us.”
        <a href="https://sunnah.com/muslim:101" target="blank">
          {" "}
          (Sahih Muslim, 101)
        </a>
        This statement highlights that reconciliation must always be rooted in
        honesty and integrity.
        <br />
        <br />
      </p>
      <table className="trade-table">
        <tr>
          <th>Aspect</th>
          <th>Sulh in Affirmation</th>
          <th>Sulh in Negation</th>
        </tr>
        <tr>
          <td>Definition</td>
          <td>
            Occurs when both parties agree that one owes a financial obligation
            or right to the other.
          </td>
          <td>
            Occurs when one party denies the claim or right of the other, often
            due to misunderstanding or dispute.
          </td>
        </tr>
        <tr>
          <td>Nature of Agreement</td>
          <td>
            The obligation is acknowledged, and reconciliation happens through
            voluntary waiver or reduction of the debt.
          </td>
          <td>
            The obligation is disputed, and reconciliation aims to resolve the
            disagreement through mutual understanding.
          </td>
        </tr>
        <tr>
          <td>Primary Objective</td>
          <td>To end conflict by showing kindness, forgiveness, or charity.</td>
          <td>
            To restore peace by resolving denial or uncertainty of rights.
          </td>
        </tr>
        <tr>
          <td>Example</td>
          <td>
            A creditor forgives half of the debt owed by a debtor to settle the
            matter peacefully.
          </td>
          <td>
            A person accused of owing money denies it forgetfully, and both
            parties agree on a reduced amount to end the dispute.
          </td>
        </tr>
        <tr>
          <td>Quranic Foundation</td>
          <td>
            “If it is difficult for someone to repay a debt, postpone it until a
            time of ease. And if you waive it as an act of charity, it will be
            better for you.” (Surah Al-Baqarah, 2:280)
          </td>
          <td>
            “The believers are but one brotherhood, so make peace between your
            brothers. And be mindful of Allah so you may be shown mercy.” (Surah
            Al-Hujurat, 49:10)
          </td>
        </tr>
      </table>
      <p className="content">
        <b>Ethical Dimensions and Contemporary Relevance of Sulh</b>
        <br />
        <br />
        Beyond resolving personal disputes, Sulh embodies Islam’s broader vision
        of a compassionate and cohesive society. It teaches believers to
        prioritize peace and forgiveness over vengeance, to listen before
        judging, and to resolve conflicts in ways that restore relationships
        rather than sever them.
        <br />
        Reconciliation plays a transformative role in communities torn by
        disagreements or financial conflicts. It nurtures empathy, encourages
        dialogue, and replaces hostility with cooperation. When families,
        neighbors, or business partners embrace Sulh, they contribute to social
        stability and collective well-being. The Prophet Muhammad (peace be upon
        him) said: <br />
        “Shall I not tell you what is better in degree than fasting, prayer, and
        charity? It is reconciliation between people, for grudges and disputes
        are the shaver.”
        <a href="https://sunnah.com/abudawud:4919" target="blank">
          {" "}
          (Sunan Abu Dawood, 4919)
        </a>{" "}
        <br />
        This hadith illustrates how deeply valued peace and unity are in
        Islam, so much so that they surpass most other voluntary acts of worship
        in their spiritual reward.
        <br />
        In today’s world, where disagreements often escalate into long-lasting
        divisions, the message of Sulh remains profoundly relevant. There are
        various modern applications of this timeless principle. They demonstrate
        how Islamic law combines justice with compassion, ensuring that disputes
        are resolved not only legally but also ethically.
        <br />
        Reconciliation also reflects the moral duty of every Muslim to protect
        the dignity of others. It teaches humility, the ability to admit
        mistakes and seek forgiveness, and magnanimity, the willingness to
        forgive even when wronged. Through Sulh, Islam creates a framework where
        truth, mercy, and social harmony coexist, ensuring that no individual or
        community suffers from prolonged animosity.
        <br />{" "}
        <BoxWithShadow>
          <strong>IMPORTANT</strong>
          <br />
          Reconciling between two disputing parties of believers is one of the
          most virtuous and rewarding acts in Islam, more virtuous that
          voluntary prayer, fasting or charity.
          <br />
        </BoxWithShadow>
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today's lesson focused on Sulh (reconciliation), which is a vital
        principle in Islamic law for resolving disputes and preserving harmony.
        We learned that Islam highly encourages reconciliation between
        individuals or groups, whether the dispute involves financial matters or
        personal disagreements. <br />
        Sulh can take two primary forms: Sulh in affirmation, where the debt or
        right is acknowledged and voluntarily waived to restore peace; and Sulh
        in negation, where a disputed claim is resolved through mutual
        understanding, provided honesty and fairness are upheld. The lesson
        emphasized that reconciliation must never compromise justice or be based
        on deceit, especially when third-party rights are involved. Overall,
        Sulh reflects Islam’s deep commitment to justice, mercy, and maintaining
        unity within the community.
        <br />
        <br />
        <b>Coming up next</b> <br />
        <br />
        In our next session, we will explore the powerful and principled concept
        of Hajr i.e the Islamic practice of freezing assets to protect the
        rights of creditors and the welfare of vulnerable individuals. We’ll
        learn how Islamic law balances justice and compassion by addressing
        cases where a debtor is either incapable, negligent, or partially able
        to repay, and how asset freezing can also serve to protect minors, the
        mentally ill, and the financially irresponsible. Rooted in both Quranic
        and Prophetic guidance, Hajr ensures that financial dealings remain
        fair, transparent, and socially responsible.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

       <FloatingPageNav 
                   prevLink={links.prev} 
                   indexLink={links.index} 
                   nextLink={links.next} 
                 />
    </div>
  );
}

export default IslamicTradePage15;
